import { type BlockConfig, iconField, linkField } from './_types';

/**
 * Payload `Block` config sidecar for the `featureGrid` block.
 *
 * Generic 2/3/4-column grid of `{icon, title, description, optional link}`
 * cards used cross-page. Decomposed from the patterns on Partners (value
 * props), Careers (benefits + perks), Download App (features), Reklaim
 * Invest, and similar surfaces.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `feature-grid.tsx` / `feature-grid.payload.ts`
 *   React component export:  `FeatureGrid`
 *   Payload `slug`:          `featureGrid`
 *   Payload `interfaceName`: `FeatureGridBlock`
 */

export const FeatureGridBlock: BlockConfig = {
  slug: 'featureGrid',
  interfaceName: 'FeatureGridBlock',
  labels: {
    singular: 'Feature Grid',
    plural: 'Feature Grid Blocks',
  },
  fields: [
    {
      name: 'eyebrow',
      type: 'text',
      label: 'Eyebrow / Badge',
    },
    {
      name: 'headline',
      type: 'textarea',
      required: true,
      label: 'Headline',
      admin: { rows: 2 },
    },
    {
      name: 'subheadline',
      type: 'textarea',
      label: 'Subheadline',
      admin: { rows: 2 },
    },
    {
      name: 'columns',
      type: 'select',
      defaultValue: '3',
      label: 'Columns',
      options: [
        { label: '2', value: '2' },
        { label: '3', value: '3' },
        { label: '4', value: '4' },
      ],
    },
    {
      name: 'iconStyle',
      type: 'select',
      defaultValue: 'tile',
      label: 'Icon style',
      options: [
        { label: 'Tile (primary background)', value: 'tile' },
        { label: 'Inline (small icon next to title)', value: 'inline' },
        // `iconless` skips the icon render entirely (centered card with
        // title + description only). Added in REK-1587 Phase-8a-fix for
        // /careers "Our Values" 3-card grid.
        { label: 'Iconless (no icon, centered card)', value: 'iconless' },
      ],
    },
    {
      name: 'bgVariant',
      type: 'select',
      defaultValue: 'default',
      label: 'Background variant',
      options: [
        { label: 'Default', value: 'default' },
        { label: 'Muted', value: 'muted' },
        { label: 'Card', value: 'card' },
      ],
    },
    {
      name: 'items',
      type: 'array',
      required: true,
      minRows: 2,
      maxRows: 12,
      label: 'Items',
      labels: { singular: 'Item', plural: 'Items' },
      fields: [
        {
          ...iconField({
            name: 'icon',
            label: 'Icon (lucide-react name)',
            // Required for `tile` + `inline` styles; optional for
            // `iconless` (the iconless render skips the icon entirely).
            // Conditional required-ness isn't expressible in this
            // BlockField shape today; documenting via admin description.
            required: false,
          }),
          admin: {
            description:
              'Allowed PascalCase lucide-react names: ArrowRight, Award, BadgeCheck, BarChart3, Bell, Bot, BookOpen, Building2, Calendar, CheckCircle2, Coffee, Compass, Crown, Database, Dumbbell, Eye, Gift, Globe, GraduationCap, Heart, Lock, Mail, MessageCircle, Palmtree, Rocket, Search, Settings, Share2, Shield, ShoppingBag, Smartphone, Sparkles, Star, Target, TrendingUp, Trophy, User, Users, Wallet, Zap. Unknown names fall back to Sparkles. Required for "Tile" + "Inline" styles; ignored for "Iconless" style.',
          },
        },
        { name: 'title', type: 'text', required: true, label: 'Title' },
        {
          name: 'description',
          type: 'textarea',
          label: 'Description',
          admin: { rows: 3 },
        },
        linkField({ name: 'link', label: 'Optional link URL' }),
        {
          name: 'linkText',
          type: 'text',
          label: 'Link text',
          admin: {
            description:
              'Optional in tile mode -- when "Link URL" is set but Link text is blank, the block falls back to "Learn more" so the CTA never silently disappears. Ignored in inline mode (the title becomes the clickable surface).',
          },
        },
        {
          name: 'external',
          type: 'checkbox',
          defaultValue: false,
          label: 'External link?',
        },
      ],
    },
  ],
};

export default FeatureGridBlock;
